import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useGetPost, useGetSpace } from '@onesource/services';
import { PostCard } from './post-card';

/**
 * Space Post Detail Page
 * Shows a single post with full content, reactions, and comments.
 * Renders through PostCard (variant="detail") so the feed and detail stay in sync.
 */
export const SpacePostDetailPage = () => {
  const navigate = useNavigate();
  const { id: spaceId, postId } = useParams({ from: '/_protected/community-hub/spaces/$id/posts/$postId' });

  const { data: post, isLoading: isLoadingPost, error: postError } = useGetPost(postId);
  const { data: space } = useGetSpace(spaceId);

  const handleBackToSpace = () => {
    navigate({ to: '/community-hub/spaces/$id/community', params: { id: spaceId } });
  };

  if (isLoadingPost) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (postError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load post: {postError.message}</Alert>
      </Box>
    );
  }

  if (!post) {
    return (
      <Box p={3}>
        <Alert severity="error">Post not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          cursor: 'pointer',
        }}
        onClick={handleBackToSpace}
      >
        <ArrowBackIcon sx={{ fontSize: 20, color: '#333' }} />
        <Typography
          sx={{
            fontFamily: 'Figtree, sans-serif',
            fontWeight: 500,
            fontSize: 14,
            color: '#333',
          }}
        >
          Back to {space?.name || 'Community'}
        </Typography>
      </Box>

      <PostCard post={post} spaceId={spaceId} variant="detail" onAfterDelete={handleBackToSpace} />
    </Box>
  );
};
